import {
	GameConfigService,
	INotificationsService,
	IPlayerService,
	IRpcService,
	notifyOnError,
} from '~/rage';
import { ClientError, ClientErrorCode } from '~/shared';
import { BackendService } from '../BackendService';
import { AbstractExchangeService } from './AbstractExchangeService';
import {
	ExchangeState,
	IExchangeData,
	IExchangeItem,
	IExchangeService,
} from './types';

export class ExchangeService
	extends AbstractExchangeService
	implements IExchangeService
{
	constructor(
		private readonly backend: BackendService,
		private readonly players: IPlayerService,
		private readonly notifications: INotificationsService,
		private readonly rpc: IRpcService,
		private readonly gameConfig: GameConfigService,
	) {
		super();

		this.backend.addEventHandler('exchange:data', (data: IExchangeData) => {
			const characterId = this.players.getCharacterId().getValue();
			if (!characterId) return;

			this.setData(data, characterId);

			const state = this.getData().state;
			if (
				state === ExchangeState.PlacingItems ||
				state === ExchangeState.ConfirmingExchange
			) {
				this.rpc.invokeClient('exchange:isNearExchange').then((isNear) => {
					if (!isNear) {
						this.notifications.showError('Вы слишком далеко от точки обмена');
						this.exitExchange();
					}
				});
			}

			if (state === ExchangeState.Completed) {
				this.exitExchange();
			}
		});

		this.backend.addEventHandler(
			'exchange:ownedItems',
			(data: { items: IExchangeItem[] }) => {
				this.setOwnedItems(data.items);
			},
		);

		this.gameConfig.getConfigs().subscribe((config) => {
			if (!config) {
				return;
			}
			this.exchangeDelaySec.next(config.exchange?.confirmationDelay ?? 5);
		});
	}

	@notifyOnError
	public async findPlayers(
		nickname?: string,
	): Promise<{ characterId: string; nickname: string }[]> {
		const players = await this.rpc.invokeClient<
			{ characterId: string; nickname: string }[]
		>('exchange:findPlayers', nickname);

		return players;
	}

	@notifyOnError
	public async invite(otherCharacterId: string): Promise<void> {
		await this.checkIsNearExchange();
		await this.backend.invoke('exchange:invite', {
			otherCharacterId,
		});
		this.notifications.showInfo(
			'Игроку было отправлено приглашение начать обмен',
		);
	}

	@notifyOnError
	public async placeItem(
		itemId: string,
		amount?: number | undefined,
	): Promise<void> {
		await this.checkIsNearExchange();
		await this.rpc.invokeServer('exchange:placeItem', { itemId, amount });
	}

	@notifyOnError
	public async removePlacedItem(itemId: string): Promise<void> {
		await this.checkIsNearExchange();
		await this.backend.invoke('exchange:removePlacedItem', { itemId });
	}

	@notifyOnError
	public async confirmItems(): Promise<void> {
		await this.checkIsNearExchange();
		await this.backend.invoke('exchange:confirmItems');
	}

	@notifyOnError
	public async confirmExchange(): Promise<void> {
		await this.checkIsNearExchange();
		await this.backend.invoke('exchange:confirmExchange');
	}

	@notifyOnError
	public async exitExchange(): Promise<void> {
		await this.rpc.invokeClient('exchange:exitMenu');
		await this.backend.invoke('exchange:cancel');
	}

	private async checkIsNearExchange(): Promise<void> {
		const isNear = await this.rpc.invokeClient('exchange:isNearExchange');
		if (!isNear) {
			await this.exitExchange();
			throw new ClientError(ClientErrorCode.ExchangeIsNotInRadius);
		}
	}
}
